import React, { createContext, useContext, useReducer } from "react";

import { User } from "../../../../domain/User";
import { ProductInstance } from "../../../../domain/ProductInstance";
import { UserGroup } from "../../../../domain/UserGroup";

export type trackedUserGroup = {
    description?: string;
    editing?: boolean;
    userGroup: UserGroup | null;
    failures?: { message: string; instanceId: string; operation: string }[];
    originalUserGroup: UserGroup | null;
    productInstances: ProductInstance[];
    users: User[];
    initialUsers?: User[];
    loading?: boolean;
};

const contextInitialValues: trackedUserGroup = {
    description: "",
    editing: false,
    userGroup: null,
    originalUserGroup: null,
    failures: [],
    productInstances: [],
    users: [],
    initialUsers: [],
    loading: false
};

type UserGroupStateUpdates = Partial<trackedUserGroup>;
// Updates can be a plain patch, or a function of the current state. The functional form is
// needed when several effects in the same commit patch the same field: each dispatch is
// replayed in order against the state the previous one produced, so the function always sees
// the latest values instead of whatever its closure captured at render time.
type UserGroupStateUpdater = UserGroupStateUpdates | ((current: trackedUserGroup) => UserGroupStateUpdates);

const userGroupReducer = (state: trackedUserGroup, updates: UserGroupStateUpdater): trackedUserGroup => {
    return {
        ...state,
        ...(typeof updates === "function" ? updates(state) : updates)
    };
};

type UserGroupContextDefinition = {
    userGroupState: trackedUserGroup;
    updateUserGroupState: Function;
};

export const UserGroupContext = createContext<UserGroupContextDefinition>({
    userGroupState: contextInitialValues,
    updateUserGroupState: () => {
        return null;
    }
});

export const UserGroupProvider = ({ children }: { children: any }) => {
    const [userGroupState, updateUserGroupState] = useReducer(userGroupReducer, contextInitialValues);

    const value = {
        userGroupState,
        updateUserGroupState
    };

    return <UserGroupContext.Provider value={value}>{children}</UserGroupContext.Provider>;
};

export const useUserGroupContext = () => {
    return useContext(UserGroupContext);
};
